import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Fade,
  IconButton,
  InputAdornment,
  CircularProgress,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageIcon from "@mui/icons-material/Image";
import { get, post } from "../../utility/fetchWeb";

const slideStyle = {
  position: "fixed",
  top: 0,
  right: 0,
  width: "33.33vw",
  height: "100vh",
  bgcolor: "#F4FCE3",
  borderLeft: "1px solid #2E7D32",
  boxShadow: 24,
  zIndex: 1300,
  p: 2,
  display: "flex",
  flexDirection: "column",
};

const LaboratoryChatModal = ({ open = false, handleClose, selectedLab = null }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Speech recognition states
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechError, setSpeechError] = useState("");
  const [showSpeechAlert, setShowSpeechAlert] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // User info from session storage
  const currentUserId = parseInt(sessionStorage.getItem("userId"));
  const currentUserType = 1; // User type for regular users

  // Lab info from selected lab
  const labId = selectedLab?.contactPersonId || selectedLab?.id || 0;
  const labUserType = selectedLab?.userType || 2;
  const labName = selectedLab?.name || `${selectedLab?.firstName + " " + selectedLab?.lastName}` || "Laboratory";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize Speech Recognition
  useEffect(() => {
    // Check if speech recognition is supported
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      
      // Configure speech recognition
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US'; // You can make this configurable
      
      // Handle speech recognition results
      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Update the message input with the transcript
        if (finalTranscript) {
          setNewMessage(prev => prev + finalTranscript);
        }
      };
      
      // Handle speech recognition errors
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        let errorMessage = '';
        switch (event.error) {
          case 'no-speech':
            errorMessage = 'No speech detected. Please try again.';
            break;
          case 'audio-capture':
            errorMessage = 'Microphone not accessible. Please check permissions.';
            break;
          case 'not-allowed':
            errorMessage = 'Microphone permission denied. Please enable microphone access.';
            break;
          case 'network':
            errorMessage = 'Network error occurred during speech recognition.';
            break;
          default:
            errorMessage = 'Speech recognition error occurred. Please try again.';
        }
        
        setSpeechError(errorMessage);
        setShowSpeechAlert(true);
      };
      
      // Handle speech recognition end
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      // Handle speech recognition start
      recognitionRef.current.onstart = () => {
        setIsListening(true);
        setSpeechError("");
      };
    } else {
      setSpeechSupported(false);
      console.warn('Speech recognition not supported in this browser');
    }
    
    // Cleanup
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Fetch conversation history when modal opens
  useEffect(() => {
    if (open && selectedLab && selectedLab.id) {
      fetchConversation();
    } else if (open && !selectedLab) {
      console.warn('LaboratoryChatModal opened without selectedLab');
    }
  }, [open, selectedLab]);

  const fetchConversation = async () => {
    if (!selectedLab || !selectedLab.id) {
      console.error('Cannot fetch conversation: selectedLab is missing or invalid');
      return;
    }

    setIsLoading(true);
    try {
      const response = await get(
        `/Posts/conversation/${currentUserId}/${currentUserType}/${labId}/${labUserType}`
      );

      console.log('API Response:', response);

      if (response && Array.isArray(response) && response.length > 0) {
        const transformedMessages = response.map((msg, index) => ({
          id: msg.id || index,
          from: msg.senderId === currentUserId ? "user" : "lab",
          text: msg.text || msg.message,
          image: msg.image || msg.imagePath,
          timestamp: msg.createdAt || new Date().toISOString(),
          senderName: msg.senderId === currentUserId ? "You" : labName,
        }));
        setMessages(transformedMessages);
        console.log('Existing conversation loaded with', response.length, 'messages');
      } else {
        console.log('No existing conversation found, setting welcome message');
        setMessages([
          {
            id: 1,
            from: "lab",
            text: `Hello! Welcome to ${labName}. How can we assist you today?`,
            timestamp: new Date().toISOString(),
            senderName: labName,
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching conversation:", error);
      console.log('Error occurred, setting welcome message');
      setMessages([
        {
          id: 1,
          from: "lab",
          text: `Hello! Welcome to ${labName}. How can we assist you today?`,
          timestamp: new Date().toISOString(),
          senderName: labName,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB.');
        return;
      }

      setSelectedImage(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Speech recognition handlers
  const startListening = () => {
    if (!speechSupported) {
      setSpeechError('Speech recognition is not supported in this browser.');
      setShowSpeechAlert(true);
      return;
    }
    
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setSpeechError('Failed to start speech recognition. Please try again.');
        setShowSpeechAlert(true);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || isSending || !selectedLab) return;

    const messageText = newMessage.trim();
    setNewMessage("");
    setIsSending(true);

    // Stop listening if currently active
    if (isListening) {
      stopListening();
    }

    const tempMessage = {
      id: Date.now(),
      from: "user",
      text: messageText,
      timestamp: new Date().toISOString(),
      senderName: "You",
      pending: true,
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const messageData = {
        senderId: currentUserId,
        senderType: currentUserType,
        receiverId: labId,
        receiverType: 2,
        text: messageText,
      };

      const response = await post("/Posts/send-text", messageData);

      if (response) {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === tempMessage.id
              ? { ...msg, pending: false, id: response.id || msg.id }
              : msg
          )
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleCloseAlert = () => {
    setShowSpeechAlert(false);
    setSpeechError("");
  };

  // Early return if no selectedLab to prevent rendering errors
  if (!selectedLab) {
    return null;
  }

  return (
    <>
      <Modal open={Boolean(open)} onClose={handleClose} hideBackdrop sx={{ zIndex: 1300 }}>
        <Fade in={open}>
          <Box sx={slideStyle}>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              pb={2}
              borderBottom="1px solid #2E7D32"
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: "#2E7D32" }}>
                  {labName.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" color="#2E7D32">
                    {labName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                   
                    {isListening && (
                      <Typography component="span" sx={{ color: '#f44336', ml: 1 }}>
                        • Listening...
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Messages Container */}
            <Box
              flex={1}
              overflow="auto"
              p={1}
              mb={2}
              bgcolor="#ffffff"
              borderRadius={2}
              boxShadow={1}
              sx={{
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#2E7D32',
                  borderRadius: '3px',
                },
              }}
            >
              {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress size={30} sx={{ color: "#2E7D32" }} />
                </Box>
              ) : (
                <>
                  {messages.map((msg) => (
                    <Box
                      key={msg.id}
                      display="flex"
                      justifyContent={msg.from === "user" ? "flex-end" : "flex-start"}
                      mb={2}
                    >
                      <Box
                        sx={{
                          maxWidth: "85%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: msg.from === "user" ? "flex-end" : "flex-start",
                        }}
                      >
                        <Box
                          px={2}
                          py={1}
                          bgcolor={msg.from === "user" ? "#DCF8C6" : "#E8F5E8"}
                          borderRadius={2}
                          position="relative"
                          sx={{
                            opacity: msg.pending ? 0.7 : 1,
                            '&::before': msg.from === "user" ? {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              right: -8,
                              width: 0,
                              height: 0,
                              border: '8px solid transparent',
                              borderTopColor: '#DCF8C6',
                            } : {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: -8,
                              width: 0,
                              height: 0,
                              border: '8px solid transparent',
                              borderTopColor: '#E8F5E8',
                            }
                          }}
                        >
                          {msg.image && (
                            <Box mb={msg.text ? 1 : 0}>
                              <img
                                src={msg.image}
                                alt="Shared image"
                                style={{
                                  maxWidth: '200px',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                }}
                                onClick={() => window.open(msg.image, '_blank')}
                              />
                            </Box>
                          )}

                          {msg.text && (
                            <Typography variant="body2">
                              {msg.text}
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, px: 1 }}
                        >
                          {msg.senderName} • {formatTimestamp(msg.timestamp)}
                          {msg.pending && " • Sending..."}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </Box>

            {/* Input Area */}
            <Box>
              {/* Image Preview */}
              {imagePreview && (
                <Box
                  mb={2}
                  p={2}
                  bgcolor="#F5F5F5"
                  borderRadius={2}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                    }}
                  />
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary">
                      Image selected
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedImage?.name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    sx={{ color: "#f44336" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Box display="flex" gap={1} alignItems="flex-end">
                <TextField
                  fullWidth
                  placeholder={isListening ? "Listening... Speak now" : "Type a message or use voice input..."}
                  variant="outlined"
                  size="small"
                  multiline
                  maxRows={3}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isSending || isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip title="Attach Image">
                          <IconButton
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isSending || isLoading}
                          >
                            <ImageIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={isListening ? "Stop Voice Input" : "Start Voice Input"}>
                          <IconButton
                            size="small"
                            onClick={handleVoiceToggle}
                            disabled={isSending || isLoading}
                            sx={{
                              color: isListening ? '#f44336' : '#2E7D32',
                              backgroundColor: isListening ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
                              '&:hover': {
                                backgroundColor: isListening ? 'rgba(244, 67, 54, 0.2)' : 'rgba(46, 125, 50, 0.1)',
                              },
                            }}
                          >
                            {isListening ? <MicOffIcon /> : <MicIcon />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isListening ? '#f44336' : '#2E7D32',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: isListening ? '#f44336' : '#2E7D32',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: isListening ? '#f44336' : '#2E7D32',
                      },
                    },
                  }}
                />
                <Button
                  onClick={handleSend}
                  variant="contained"
                  disabled={(!newMessage.trim() && !selectedImage) || isSending || isLoading}
                  sx={{
                    backgroundColor: "#2E7D32",
                    color: "white",
                    minWidth: 48,
                    height: 40,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: "#1B5E20",
                    },
                    '&:disabled': {
                      backgroundColor: "#C8E6C9",
                    },
                  }}
                >
                  {isSending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SendIcon />
                  )}
                </Button>
              </Box>

              {/* Hidden File Input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </Box>

            {/* Footer */}
            <Box mt={2} textAlign="center">
              <Typography variant="caption" color="text.secondary">
                End-to-end encrypted • Powered by Connected Health Pro
                {speechSupported && (
                  <Typography component="span" sx={{ display: 'block', mt: 0.5 }}>
                    Voice input available • Click mic to speak
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Speech Error Alert */}
      <Snackbar
        open={showSpeechAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {speechError}
        </Alert>
      </Snackbar>
    </>
  );
};

// PropTypes for better validation
LaboratoryChatModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  selectedLab: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    contactPersonId: PropTypes.number,
    userType: PropTypes.number,
  }),
};

// Default props
LaboratoryChatModal.defaultProps = {
  open: false,
  selectedLab: null,
};

export default LaboratoryChatModal;